import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { useApiMutation } from '../../../shared/useApi.js';
import { toast } from '../../../shared/useToast.js';

const KEY = ['cards'];

export function useCards(params = {}) {
  return useQuery({
    queryKey: [...KEY, params],
    queryFn: async () => {
      const { data } = await api.get('/cards', { params });
      return data || [];
    },
    staleTime: 15_000
  });
}

/**
 * كشف حساب البطاقة.
 *
 * المسار يُعيد `{ card, owner, movements, totals, reconciliation, ... }` بلا
 * مفتاح `items`، فلا يفكّه مُعترض `shared/api.js` إلى مصفوفة وتبقى المجاميع
 * ومعلومات البطاقة متاحة.
 *
 * `staleTime: 0` مقصود: الكشف مستند مالي يُقرأ ويُطبع، ويجب أن يعكس أي تعبئة
 * أو بيع حدث قبل ثانية — لا نسخة مخزَّنة.
 */
export function useCardStatement(cardId, params = {}, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'statement', cardId, params],
    enabled: !!cardId && enabled,
    queryFn: async () => {
      const { data } = await api.get(`/cards/${cardId}/statement`, { params });
      return data;
    },
    staleTime: 0
  });
}

export function useCreateCard() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => api.post('/cards', body).then((r) => r.data),
    successMessage: 'تم إضافة الكارت',
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY })
  });
}

export function useCreateBulkCards() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => api.post('/cards/bulk', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY })
  });
}

export function useUpdateCard() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, body }) => api.patch(`/cards/${id}`, body).then((r) => r.data),
    successMessage: 'تم حفظ التعديلات',
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY })
  });
}

/**
 * تسجيل كارت كمفقود.
 *
 * كان تبويب «المفقودة» فارغاً دائماً لأن الحالة `lost` موجودة في النموذج
 * والفلتر والخادم، لكن لا زرّ في أي شاشة يضبطها — فلا كارت يصل إليها أصلاً.
 *
 * `refetchType: 'active'` ضروري: بدونه تبقى القائمة المعروضة على نسختها
 * المخزَّنة حتى ينتهي staleTime، فيبدو الكارت وكأنه لم يُسجَّل. و`await`
 * يضمن أن النافذة لا تُغلق قبل أن تصل البيانات الجديدة.
 */
export function useMarkCardLost() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id }) => api.patch(`/cards/${id}`, { status: 'lost' }).then((r) => r.data),
    successMessage: 'تم تسجيل الكارت كمفقود — يظهر الآن في تبويب «المفقودة»',
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY, refetchType: 'active' });
    }
  });
}

// القاصات المسموح استلام تعبئة الكروت فيها.
// نستعمل نقطة نهاية مخصّصة لأن GET /cashbox يتطلب صلاحية cashbox.view
// التي لا يملكها موظف الاستقبال عادةً.
export function useTopupCashboxes(enabled = true) {
  return useQuery({
    queryKey: ['cards', 'topup-cashboxes'],
    enabled,
    queryFn: async () => {
      const { data } = await api.get('/cards/topup-cashboxes');
      return data || [];
    },
    staleTime: 60_000
  });
}

export function useTopupCard() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, amount, cashboxId }) =>
      api.post(`/cards/${id}/topup`, { amount, cashboxId: cashboxId || undefined }).then((r) => r.data),
    successMessage: 'تم شحن الكارت ودخل المبلغ للقاصة',
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      // التعبئة تزيد رصيد القاصة وتُسجَّل إيراداً — حدِّث الشاشات المعنية.
      qc.invalidateQueries({ queryKey: ['cashboxes'] });
      qc.invalidateQueries({ queryKey: ['finance'] });
      qc.invalidateQueries({ queryKey: ['dash'] });
    }
  });
}

export function useBlockCard() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id) => api.post(`/cards/${id}/block`).then((r) => r.data),
    successMessage: 'تم حظر الكارت',
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY })
  });
}

export function useReleaseCard() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id) => api.post(`/cards/${id}/release`).then((r) => r.data),
    successMessage: 'تم تحرير الكارت',
    // نفس سبب `refetchType` في تسجيل الفقدان: الكارت يغادر التبويب المعروض
    // فوراً، لا بعد انتهاء staleTime.
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY, refetchType: 'active' });
    }
  });
}

export function useDeleteCard() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id) => api.delete(`/cards/${id}`).then((r) => r.data),
    // الخادم يشرح ما حدث بالضبط (حذف فعلي أم إلغاء لأن للكارت سجلاً)،
    // فنعرض رسالته بدل نص ثابت يخفي الفرق.
    onSuccess: async (data) => {
      // رسالة الإلغاء أطول من سطر — أعطها وقتاً كافياً للقراءة.
      const mode = data && data.mode;
      toast.success((data && data.message) || 'تم حذف الكارت', mode === 'deactivated' ? 9000 : undefined);
      // refetchType: 'active' يعيد جلب القائمة المعروضة فوراً لا عند التركيز،
      // وإلا بقي الكارت المحذوف ظاهراً حتى ينتهي staleTime.
      await qc.invalidateQueries({ queryKey: KEY, refetchType: 'active' });
    }
  });
}
